/**
 * ADR-0263 S4 — shared helper for the merchant control-plane
 * `plan_feature_required` 403.
 *
 * The server returns:
 *   HTTP 403 { error: 'plan_feature_required', feature, plan, message }
 * where `plan` is the cheapest active site plan that includes the
 * feature, or `null` when the v2 catalog has no match. The
 * dashboard's http-client parses the body into `ApiError.server_body`
 * and `ApiError.server_reason`.
 *
 * Views call `planFeatureRequiredDetails(err)`. A non-matching error
 * returns `null`. A match returns the feature code + optional plan
 * name + a plain-copy message the view can render inline.
 */

import { ApiError } from './http-client.js';

export interface PlanFeatureRequiredDetails {
  readonly feature: string;
  readonly plan_name: string | null;
  readonly message: string;
}

/** True iff `err` is a `plan_feature_required` 403. */
export function isPlanFeatureRequired(err: unknown): err is ApiError {
  return err instanceof ApiError
    && err.kind === 'forbidden'
    && err.server_reason === 'plan_feature_required';
}

/** Parse the server body into the view-ready shape, or `null` when
 *  the error is unrelated. */
export function planFeatureRequiredDetails(
  err: unknown,
): PlanFeatureRequiredDetails | null {
  if (!isPlanFeatureRequired(err)) return null;
  const body = err.server_body ?? {};
  const feature = typeof (body as { feature?: unknown }).feature === 'string'
    ? (body as { feature: string }).feature
    : 'feature';
  const plan = (body as { plan?: unknown }).plan;
  const plan_name = plan !== null && typeof plan === 'object'
    && typeof (plan as { name?: unknown }).name === 'string'
    ? (plan as { name: string }).name
    : null;
  const message = typeof (body as { message?: unknown }).message === 'string'
    ? (body as { message: string }).message
    : plan_name !== null
      ? `This feature is part of the ${plan_name} plan.`
      : "This feature is not on this site's plan.";
  return { feature, plan_name, message };
}
